import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PermitLetter } from '../../types';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';

interface PersonnelLetterFormProps {
  letter?: PermitLetter;
  onSubmit: (data: Partial<PermitLetter>) => void;
  onCancel: () => void;
}

const PersonnelLetterForm: React.FC<PersonnelLetterFormProps> = ({ letter, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<PermitLetter>({
    defaultValues: letter || {
      templateName: '',
      subject: '',
      content: '',
      isDefault: false
    }
  });

  useEffect(() => {
    if (letter) {
      reset(letter);
    }
  }, [letter, reset]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {letter ? 'Edit Letter Template' : 'Create New Letter Template'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Template Name */}
            <div>
              <Input
                label="Template Name"
                {...register('templateName', { required: 'Template name is required' })}
                error={errors.templateName?.message}
                placeholder="Enter template name"
              />
            </div>

            {/* Default Template Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isDefault')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Set as default template
              </label>
            </div>

            {/* Subject */}
            <div className="md:col-span-2">
              <Input
                label="Subject"
                {...register('subject', { required: 'Subject is required' })}
                error={errors.subject?.message}
                placeholder="Enter letter subject"
              />
            </div>

            {/* Content */}
            <div className="md:col-span-2">
              <TextArea
                label="Letter Content"
                {...register('content', { required: 'Content is required' })}
                error={errors.content?.message}
                placeholder="Enter letter content. Use {{EMPLOYEE_DETAILS}} as placeholder for employee information."
                rows={12}
              />
              <div className="mt-2 text-sm text-gray-500">
                <p><strong>Available placeholders:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li><code>{'{{EMPLOYEE_DETAILS}}'}</code> - Will be replaced with selected employee details</li>
                  <li><code>{'{{DATE}}'}</code> - Will be replaced with current date</li>
                  <li><code>{'{{COMPANY_NAME}}'}</code> - Will be replaced with company name</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {letter ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonnelLetterForm;